import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { EmptyState } from "@/components/Bits";
import { Bell, Trash2, History as HistoryIcon } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  ["price_above", "alerts.priceAbove", true],
  ["price_below", "alerts.priceBelow", true],
  ["price_target", "alerts.priceTarget", true],
  ["buy_signal", "alerts.buySignal", false],
  ["sell_signal", "alerts.sellSignal", false],
  ["volatility", "alerts.volatility", false],
];

export default function AppAlerts() {
  const { t } = useLang();
  const [alerts, setAlerts] = useState([]);
  const [history, setHistory] = useState([]);
  const [assets, setAssets] = useState([]);
  const [f, setF] = useState({ symbol: "BTC/USD", type: "price_above", value: "", channel: "in_app" });

  const load = () => {
    api.get("/alerts").then((r) => setAlerts(r.data.alerts)).catch(() => {});
    api.get("/alerts/history").then((r) => setHistory(r.data.history)).catch(() => {});
  };
  useEffect(() => { load(); api.get("/markets").then((r) => setAssets(r.data.assets)).catch(() => {}); }, []);

  const needsValue = TYPES.find((x) => x[0] === f.type)?.[2];

  const create = async () => {
    try {
      await api.post("/alerts", { symbol: f.symbol, type: f.type, value: f.value ? +f.value : null, channel: f.channel });
      toast.success(t("alerts.create")); setF({ ...f, value: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = async (id) => { await api.patch(`/alerts/${id}/toggle`); load(); };
  const remove = async (id) => { await api.delete(`/alerts/${id}`); load(); };

  const statusBadge = (s) => {
    const map = { active: "bg-bullish/15 text-bullish", triggered: "bg-primary/15 text-primary", disabled: "bg-secondary text-muted-foreground" };
    return <span className={`rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase ${map[s] || map.disabled}`}>{s}</span>;
  };

  return (
    <DashboardLayout title={t("alerts.title")}>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="font-display text-lg font-semibold">{t("alerts.newAlert")}</h3>
          <div className="mt-4 space-y-3 text-sm">
            <div>
              <label className="mb-1 block text-[10px] uppercase text-muted-foreground">Asset</label>
              <select value={f.symbol} onChange={(e) => setF({ ...f, symbol: e.target.value })} data-testid="alert-symbol" className="w-full rounded-lg border border-border bg-card px-3 py-2">
                {assets.map((a) => <option key={a.symbol} value={a.symbol}>{a.symbol}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[10px] uppercase text-muted-foreground">{t("alerts.type")}</label>
              <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="alert-type" className="w-full rounded-lg border border-border bg-card px-3 py-2">
                {TYPES.map(([v, k]) => <option key={v} value={v}>{t(k)}</option>)}
              </select>
            </div>
            {needsValue && (
              <div>
                <label className="mb-1 block text-[10px] uppercase text-muted-foreground">{t("alerts.value")}</label>
                <input type="number" step="any" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid="alert-value" className="w-full rounded-lg border border-border bg-card px-3 py-2 font-mono" />
              </div>
            )}
            <div>
              <label className="mb-1 block text-[10px] uppercase text-muted-foreground">{t("alerts.channel")}</label>
              <select value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })} data-testid="alert-channel" className="w-full rounded-lg border border-border bg-card px-3 py-2">
                <option value="in_app">{t("alerts.inApp")}</option>
                <option value="email">{t("alerts.email")}</option>
                <option value="telegram">{t("alerts.telegram")}</option>
              </select>
            </div>
            <button onClick={create} data-testid="alert-create" className="w-full rounded-xl bg-primary py-2.5 font-semibold text-primary-foreground">{t("alerts.create")}</button>
            <p className="text-xs text-muted-foreground">Alerts are evaluated by a background job every minute and survive restarts. Connect Telegram in Settings to receive Telegram alerts.</p>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          {alerts.length === 0 ? (
            <EmptyState title={t("alerts.empty")} description={t("alerts.emptyDesc")} testid="alerts-empty" />
          ) : (
            <div className="space-y-2">
              {alerts.map((a) => (
                <div key={a.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-4" data-testid={`alert-${a.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10"><Bell className="h-4 w-4 text-accent" /></div>
                    <div>
                      <div className="font-semibold">{a.symbol} · {t(TYPES.find((x) => x[0] === a.type)?.[1] || a.type)}</div>
                      <div className="text-xs text-muted-foreground">{a.value != null ? `${a.value} · ` : ""}{a.channel} · {a.trigger_count || 0}×</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {statusBadge(a.status)}
                    <button onClick={() => toggle(a.id)} data-testid={`alert-toggle-${a.id}`} className={`h-6 w-11 rounded-full p-0.5 transition-colors ${a.enabled ? "bg-primary" : "bg-secondary"}`}>
                      <span className={`block h-5 w-5 rounded-full bg-white transition-transform ${a.enabled ? "translate-x-5" : ""}`} />
                    </button>
                    <button onClick={() => remove(a.id)} data-testid={`alert-del-${a.id}`} className="text-muted-foreground hover:text-bearish"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="mb-3 flex items-center gap-2"><HistoryIcon className="h-4 w-4 text-muted-foreground" /><h3 className="font-display text-lg font-semibold">{t("alerts.history")}</h3></div>
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground">No triggered alerts yet.</p>
            ) : (
              <div className="space-y-2 text-sm" data-testid="alert-history">
                {history.map((h) => (
                  <div key={h.id} className="flex items-center justify-between border-b border-border py-2 last:border-0">
                    <div><div className="font-medium">{h.title}</div><div className="text-xs text-muted-foreground">{new Date(h.at).toLocaleString()}</div></div>
                    <div className="flex gap-1">{Object.entries(h.delivery || {}).map(([k, v]) => <span key={k} className={`rounded px-1.5 py-0.5 text-[10px] ${v ? "bg-bullish/15 text-bullish" : "bg-bearish/15 text-bearish"}`}>{k}</span>)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
